/**
 * App header — Reppr Elite shell (45px, sticky).
 *
 * The ledger strip carries live portfolio figures. AUM is computed from real
 * contracts; Fees YTD has no underlying data (no commission ledger entries and no
 * commission rate on contracts) so it renders an em dash rather than a guess.
 */

import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listContracts } from '../api/contracts'
import { listDeals } from '../api/deals'
import { listClients } from '../api/clients'
import { useSession } from '../hooks/useSession'
import { queryKeys } from '../utils/queryKeys'
import { computeDeskMetrics, fmtMoney } from '../utils/deskMetrics'
import { listCommissionLedger } from '../api/commissions'
import { feesEarned, yearToDate } from '../utils/feeIncome'
import styles from './AppHeader.module.css'

/** Screen title + subtitle, keyed by route. Longest prefix wins. */
const SCREENS: { path: string; title: string; subtitle: string }[] = [
  { path: '/dashboard', title: 'Needs you', subtitle: 'Every decision waiting on you, settled in one tap — then where the book stands' },
  { path: '/outreach', title: 'Outreach', subtitle: 'Who to reach out to today, why, and a message to send' },
  { path: '/audience', title: 'Audience', subtitle: 'Every athlete’s accounts, where each number comes from, and what would fill the gaps' },
  { path: '/portal-access', title: 'Portal access', subtitle: 'Who is in the athlete portal, who can be invited, and what is holding the rest back' },
  { path: '/needs-you', title: 'Needs you', subtitle: 'Every decision waiting on you, each settled in one tap' },
  { path: '/signals', title: 'Signals', subtitle: 'Ranked by exposure — not by recency' },
  { path: '/activities', title: 'Activity', subtitle: 'Every logged interaction across the agency' },
  { path: '/notifications', title: 'Notifications', subtitle: 'Alerts, mentions and reminders' },
  { path: '/clients', title: 'The Book', subtitle: 'The athletes you represent and what they are worth' },
  { path: '/prospects', title: 'Prospects', subtitle: 'Recruiting pipeline from sourcing to signature' },
  { path: '/contacts', title: 'Contacts', subtitle: 'People across teams, brands and representation' },
  { path: '/organizations', title: 'Organizations', subtitle: 'Teams, brands, agencies and counterparties' },
  { path: '/relationships', title: 'Network', subtitle: 'Relationship health and coverage across the book' },
  { path: '/deals/templates', title: 'Deal templates', subtitle: 'Reusable structures for common deals' },
  { path: '/deals/win-loss', title: 'Win / loss', subtitle: 'What closed, what did not, and why' },
  { path: '/deals', title: 'Pipeline', subtitle: 'Every open structure by stage and weighted value' },
  { path: '/player-transactions', title: 'Player moves', subtitle: 'Signings, trades, releases and tags' },
  { path: '/contracts', title: 'Register', subtitle: 'Executed contracts, terms and key dates' },
  { path: '/incentives', title: 'Incentives', subtitle: 'Escalators, bonuses and progress against them' },
  { path: '/compliance', title: 'Compliance', subtitle: 'Filings, certifications and league deadlines' },
  { path: '/revenue', title: 'Revenue & fees', subtitle: 'What the book has earned and what is booked' },
  { path: '/invoices', title: 'Invoices', subtitle: 'Raised, outstanding and settled' },
  { path: '/payments', title: 'Payments', subtitle: 'Money in and money out' },
  { path: '/commissions', title: 'Ledger', subtitle: 'Commission splits, expenses and quota' },
  { path: '/calendar', title: 'Calendar', subtitle: 'League dates, meetings and deadlines' },
  { path: '/tasks', title: 'Tasks', subtitle: 'Everything owed by you and to you' },
  { path: '/client-inbox', title: 'Inbox', subtitle: 'Messages from the athletes you represent' },
  { path: '/vendors', title: 'Vendors', subtitle: 'Trusted services for your athletes' },
  { path: '/goals', title: 'Goals', subtitle: 'Targets and progress against them' },
  { path: '/analytics', title: 'Analytics', subtitle: 'Revenue, pipeline and portfolio performance' },
  { path: '/reports', title: 'Reports', subtitle: 'Generated summaries and shareable packages' },
  { path: '/team', title: 'Team', subtitle: 'Members, roles and commission splits' },
  { path: '/settings', title: 'Settings', subtitle: 'Account, agency and integrations' },
  { path: '/tags', title: 'Tags', subtitle: 'Labels used across the book' },
  { path: '/admin/data-health', title: 'Data health', subtitle: 'Completeness and sync status across records' },
  { path: '/help', title: 'Help & support', subtitle: 'Guides and how to reach us' },
  { path: '/search', title: 'Search', subtitle: 'Across every record in the agency' },
]

function screenFor(pathname: string) {
  const hit = SCREENS
    .filter(s => pathname === s.path || pathname.startsWith(s.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return hit ?? { title: 'Reppr', subtitle: '' }
}

export default function AppHeader() {
  const { pathname } = useLocation()
  const { user } = useSession()
  const hasAgency = !!(user as any)?.agency_id
  const screen = screenFor(pathname)

  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])

  const contractsQ = useQuery({ queryKey: queryKeys.contracts.list(), queryFn: () => listContracts(), enabled: hasAgency })
  const dealsQ = useQuery({ queryKey: queryKeys.deals.list(), queryFn: () => listDeals(), enabled: hasAgency })
  const clientsQ = useQuery({ queryKey: queryKeys.clients.list(), queryFn: () => listClients(), enabled: hasAgency })
  const ledgerQ = useQuery({ queryKey: queryKeys.commissionLedger.list(), queryFn: () => listCommissionLedger(), enabled: hasAgency })

  const metrics = useMemo(
    () => computeDeskMetrics(contractsQ.data ?? [], dealsQ.data ?? [], clientsQ.data ?? [], feesEarned(ledgerQ.data ?? [], yearToDate()).gross),
    [contractsQ.data, dealsQ.data, clientsQ.data, ledgerQ.data],
  )

  const ready = !contractsQ.isLoading && !dealsQ.isLoading
  const clock = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  const day = now.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase()

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <h1 className={styles.title}>{screen.title}</h1>
        {screen.subtitle && <span className={styles.divider} aria-hidden="true" />}
        {screen.subtitle && <p className={styles.subtitle}>{screen.subtitle}</p>}
      </div>

      <div className={styles.ledger}>
        <div className={styles.cell}>
          <span className={styles.cellLabel}>AUM</span>
          <span className={styles.cellValue}>{ready ? fmtMoney(metrics.aum) : '—'}</span>
        </div>
        <div className={styles.cell}>
          <span className={styles.cellLabel}>Fees YTD</span>
          <span
            className={`${styles.cellValue} ${metrics.feesYtd == null ? styles.cellValueMuted : ''}`}
            title={metrics.feesYtd == null ? 'No commission booked in the ledger this year' : 'Commission booked this year, from the ledger'}
          >
            {ready ? fmtMoney(metrics.feesYtd) : '—'}
          </span>
        </div>
        <div className={styles.clockCell}>
          <span className={styles.clock}>{day} · {clock} ET</span>
          <span className={styles.dot} aria-hidden="true" />
        </div>
      </div>
    </header>
  )
}
